import { useState } from 'react';
import { Link } from 'react-router';
import { spendingAccounts } from '@tahi/core';
import { setPaymentsSettings } from '../../../data/commands/payments';
import { useCommandEnv, useUsable } from '../../../data/hooks';
import { Chip, ScreenHeader } from '../../../ui';
import { useAccounts, useBudgetSettings } from '../../budget/hooks';
import { CARD, FOCUS, SCREEN } from '../../spending/styles';
import { copy } from '../copy';
import { PAYMENTS_BASE, usePaymentsSettings, useToday } from '../hooks';

const BACK = { to: PAYMENTS_BASE, label: copy.title };

const REMIND_CHOICES = [1, 2, 3, 5, 7, 14];

/** v0.7.4 /m/payments/settings: "Pay from by default" and the remind days new payees start with. */
export function PaymentsSettingsScreen() {
  const env = useCommandEnv();
  const usable = useUsable();
  const today = useToday();
  const settings = usePaymentsSettings();
  const budget = useBudgetSettings();
  const allAccounts = useAccounts();
  const [error, setError] = useState<string | null>(null);

  if (!settings) {
    return (
      <div className={SCREEN}>
        <ScreenHeader title={copy.settingsTitle} back={BACK} />
        <p className="text-muted">{copy.loading}</p>
      </div>
    );
  }
  const accounts = spendingAccounts(allAccounts ?? [], settings.defaultAccountId, today);
  const budgetName = (allAccounts ?? []).find((a) => a.id === budget?.defaultAccountId)?.name;
  const choices = REMIND_CHOICES.includes(settings.remindDays)
    ? REMIND_CHOICES
    : [...REMIND_CHOICES, settings.remindDays].sort((a, b) => a - b);

  async function save(patch: Parameters<typeof setPaymentsSettings>[1]) {
    const r = await setPaymentsSettings(env, patch);
    setError(r.ok ? null : copy.settingsError);
  }

  return (
    <div className={SCREEN}>
      <ScreenHeader title={copy.settingsTitle} back={BACK} />

      <section aria-label={copy.payFromDefault} className={CARD}>
        <h2 className="text-[20px] font-bold text-fg">{copy.payFromDefault}</h2>
        <p className="mt-1 text-sm text-muted">{copy.payFromDefaultHelp}</p>
        {!usable('budget') || accounts.length === 0 ? (
          <p className="mt-2 text-sm text-muted">
            {copy.addAccountsFirst}{' '}
            {usable('budget') ? (
              <Link to="/m/budget/accounts/new" className={`text-primary ${FOCUS}`}>
                {copy.addAccount}
              </Link>
            ) : null}
          </p>
        ) : (
          <div role="group" aria-label={copy.payFromDefault} className="mt-2 flex flex-wrap gap-2">
            <Chip
              selected={settings.defaultAccountId === null}
              aria-pressed={settings.defaultAccountId === null}
              onClick={() => void save({ defaultAccountId: null })}
            >
              {budgetName ? copy.sameAsBudgetNamed(budgetName) : copy.sameAsBudget}
            </Chip>
            {accounts.map((a) => (
              <Chip
                key={a.id}
                selected={settings.defaultAccountId === a.id}
                aria-pressed={settings.defaultAccountId === a.id}
                onClick={() => void save({ defaultAccountId: a.id })}
              >
                {a.name}
              </Chip>
            ))}
          </div>
        )}
      </section>

      <section aria-label={copy.remindTitle} className={CARD}>
        <h2 className="text-[20px] font-bold text-fg">{copy.remindTitle}</h2>
        <p className="mt-1 text-sm text-muted">{copy.remindHelp}</p>
        <div role="group" aria-label={copy.remindTitle} className="mt-2 flex flex-wrap gap-2">
          {choices.map((n) => (
            <Chip
              key={n}
              selected={settings.remindDays === n}
              aria-pressed={settings.remindDays === n}
              onClick={() => void save({ remindDays: n })}
            >
              {copy.daysN(n)}
            </Chip>
          ))}
        </div>
      </section>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
